'use client'

import ExternalLink from '@susu/headless-commerce/components/ExternalLink/ExternalLink'
import { ResponsiveImage } from '@susu/headless-commerce/components/ResponsiveImage/ResponsiveImage'
import { generateImageSourcesForCloudinary } from '@susu/headless-commerce/components/ResponsiveImage/utils'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import type {
  ImageWithText,
  MediaWrapperV2,
  NavigationLink,
  PromotionEvents,
} from '@susu/headless-commerce/gql/generated/graphql'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { generateUrlFromLinkContent } from '@susu/headless-commerce/utils/UrlGenerator'
import {
  trackClickPromotion,
  trackImpressionPromotion,
} from '@susu/headless-commerce/utils/tracking/tracking'
import classNames from 'classnames'
import type { MouseEventHandler } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import styles from './CategoryCard.module.scss'

export type CardVariation = 'regular' | 'tuxedo'
export type CategoryCardProps = {
  card: ImageWithText
}

export default function CategoryCard({ card }: Readonly<CategoryCardProps>) {
  const country = useCountry()
  const locale = useLocale()
  const pageType = usePageType()
  const sizes = useMemo(
    () => ({
      xs: '75vw',
      lg: '25vw',
    }),
    [],
  )
  const cardRef = useRef<HTMLAnchorElement>(null)
  const style = useMemo(() => ({ display: 'flex' }), [])

  useEffect(() => {
    if (cardRef?.current) {
      trackImpressionPromotion(
        cardRef,
        card?.link?.promotionEvents as PromotionEvents,
        pageType,
        {
          locale,
          country,
        },
      )
    }
  }, [card?.link?.promotionEvents, country, locale, pageType])

  const onClick: MouseEventHandler<HTMLAnchorElement> = useCallback(() => {
    trackClickPromotion(
      cardRef,
      card?.link?.promotionEvents as PromotionEvents,
      pageType,
      {
        locale,
        country,
      },
    )
  }, [card?.link?.promotionEvents, country, locale, pageType])

  if (!card) {
    return null
  }

  if (!card.image) {
    throw new Error('Category card image is missing')
  }

  const { image, title, link, colorPalette } = card

  const {
    cloudinaryDesktopImage,
    cloudinaryMobileImage,
    cloudinaryTabletImage,
  } = image as MediaWrapperV2
  const {
    desktopSrcSet,
    mobileSrcSet,
    tabletSrcSet,
    desktopSizes,
    mobileSizes,
    tabletSizes,
    smallestUrl,
  } = generateImageSourcesForCloudinary(
    {
      cloudinaryDesktopImage,
      cloudinaryMobileImage,
      cloudinaryTabletImage,
    },
    sizes,
  )
  const titleClass = classNames(
    'body-medium-sm',
    'title-03-medium-lg',
    styles.title,
    styles[`palette-${colorPalette}`],
  )

  return (
    <ExternalLink
      href={generateUrlFromLinkContent(
        link as NavigationLink,
        country.siteID,
        locale,
      )}
      className={styles.card}
      automationId={String(link?.automationId)}
      onClick={onClick}
      ref={cardRef}
    >
      <ResponsiveImage
        alt={`Content page category card ${title}`}
        desktopSizes={desktopSizes}
        desktopSrcSet={desktopSrcSet}
        lazyloading={true}
        mobileSizes={mobileSizes}
        mobileSrcSet={mobileSrcSet}
        pictureStyle={style}
        src={smallestUrl}
        tabletSizes={tabletSizes}
        tabletSrcSet={tabletSrcSet}
      />
      <div className={titleClass}>{title}</div>
    </ExternalLink>
  )
}
